/**
 * Access, on the Data model page: what is hidden from whom, in one place.
 *
 * Two lists. The first is every entity with something hidden, so an admin can
 * see the whole picture without opening entities one by one; each row opens
 * the entity, where it is changed. The second is the tables no entity is
 * built from. Chat can still query those, so they need the same choice — and
 * a table the model has since dropped, but which is still hidden, has to show
 * somewhere to be unhidden.
 *
 * Drawn for admins only. Everyone else gets nothing, not an empty box.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';

import { ListTable, Panel, SearchField, type ListColumn } from '../../ui-kit';
import {
  joinNames,
  useDataModelAccess,
  type EntityAccess,
  type OtherTableAccess,
} from './useDataModelAccess';
import { VisibleToPicker } from './VisibleToPicker';

export function DataModelAccessPanel() {
  const navigate = useNavigate();
  const { canManage, access, loading, error, setTable } = useDataModelAccess();
  const [query, setQuery] = React.useState('');

  if (!canManage) return null;

  const roles = access?.roles ?? [];
  const nameOf = (id: string) => roles.find((r) => r.role_id === id)?.name ?? 'A removed role';

  const restricted = (access?.entities ?? []).filter(
    (e) => e.hidden_role_ids.length > 0 || e.partial_role_ids.length > 0,
  );
  const hiddenFieldsFor = (e: EntityAccess, roleId: string) =>
    e.fields.filter((f) => f.hidden_role_ids.includes(roleId)).length;

  const entityColumns: ListColumn<EntityAccess>[] = [
    { key: 'name', header: 'Entity', grow: 1, minWidth: 160, render: (e) => e.name, sortValue: (e) => e.name.toLowerCase() },
    {
      key: 'what',
      header: 'What is hidden',
      grow: 3,
      minWidth: 240,
      render: (e) => (
        <Box>
          {e.hidden_role_ids.length > 0 && (
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
              The whole entity, from {joinNames(e.hidden_role_ids.map(nameOf))}
            </Typography>
          )}
          {e.partial_role_ids.map((id) => {
            const n = hiddenFieldsFor(e, id);
            return (
              <Typography key={id} variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                {n} {n === 1 ? 'field' : 'fields'}, from {nameOf(id)}
              </Typography>
            );
          })}
        </Box>
      ),
    },
  ];

  const needle = query.trim().toLowerCase();
  const tables = (access?.other_tables ?? []).filter(
    (t) => !needle || `${t.table_name} ${t.connector_name}`.toLowerCase().includes(needle),
  );
  const tableColumns: ListColumn<OtherTableAccess>[] = [
    {
      key: 'table',
      header: 'Table',
      grow: 2,
      minWidth: 200,
      render: (t) => (
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{t.table_name}</Typography>
      ),
      sortValue: (t) => t.table_name.toLowerCase(),
    },
    {
      key: 'source',
      header: 'Source',
      grow: 1,
      minWidth: 140,
      render: (t) => t.connector_name,
      sortValue: (t) => t.connector_name.toLowerCase(),
    },
    {
      key: 'visible',
      header: 'Role access',
      grow: 2,
      minWidth: 200,
      render: (t) => (
        <VisibleToPicker
          subject={t.table_name}
          roles={roles}
          hiddenRoleIds={t.hidden_role_ids}
          hidingWarning="The whole table will be hidden from the roles you untick, in chat and anywhere else a question is asked of the data."
          onApply={(changes) => setTable(t.connector_id, t.table_name, changes)}
        />
      ),
    },
  ];

  return (
    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Panel
        title="Who cannot see what"
        subtitle={
          access && !access.enforced
            ? 'Access rules are switched off for this workspace, so these choices are saved but not applied yet. Switch on Role-Based Table Access under Settings, Features.'
            : 'Every role sees everything unless it is listed here. Open an entity to change who sees it or its fields.'
        }
        infoTone={access && !access.enforced ? 'warning' : undefined}
        loading={loading}
        collapsible
        storageKey="data-model.access.entities"
      >
        {error ? (
          <Typography variant="body2" color="error" sx={{ p: 2 }}>{error}</Typography>
        ) : (
          <ListTable
            columns={entityColumns}
            rows={restricted}
            getRowKey={(e) => e.name}
            onRowClick={(e) => navigate(`/data-model/entity/${encodeURIComponent(e.name)}`)}
            emptyMessage="Nothing in the data model is hidden from any role."
            defaultSort={{ key: 'name', direction: 'asc' }}
          />
        )}
      </Panel>

      <Panel
        title="Tables outside the data model"
        subtitle="No entity is built from these, but a question in chat can still reach them. Hide a table here to keep a role out of it."
        loading={loading}
        collapsible
        defaultCollapsed
        storageKey="data-model.access.other-tables"
        search={
          (access?.other_tables.length ?? 0) > 8
            ? <SearchField value={query} onChange={setQuery} placeholder="Search tables…" />
            : undefined
        }
      >
        {!error && (
          <ListTable
            columns={tableColumns}
            rows={tables}
            getRowKey={(t) => `${t.connector_id}:${t.table_name}`}
            emptyMessage={needle ? 'No table matches.' : 'Every table we know of is part of the data model.'}
            defaultSort={{ key: 'table', direction: 'asc' }}
            pageSize={25}
          />
        )}
      </Panel>
    </Box>
  );
}
